import React, { useState } from 'react'
import emailjs from '@emailjs/browser'

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const initialForm = { name: '', email: '', message: '' }
const initialErrors = { name: '', email: '', message: '' }

const DANGEROUS_PATTERN = /<[^>]*>|javascript:|on\w+\s*=|(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER)\b)/i
const NAME_PATTERN = /^[a-zA-Z\s'.-]{2,50}$/
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const sanitize = (value) => value.trim().replace(/\s+/g, ' ')

const validateField = (field, value) => {
  const cleaned = sanitize(value)

  if (field === 'name') {
    if (!cleaned) return 'Name is required.'
    if (cleaned.length < 2) return 'Name must be at least 2 characters.'
    if (cleaned.length > 50) return 'Name cannot exceed 50 characters.'
    if (!NAME_PATTERN.test(cleaned)) return 'Name can only contain letters and basic punctuation.'
    if (DANGEROUS_PATTERN.test(cleaned)) return 'Invalid characters detected in name.'
    return ''
  }

  if (field === 'email') {
    if (!cleaned) return 'Email is required.'
    if (cleaned.length > 100) return 'Email cannot exceed 100 characters.'
    if (!EMAIL_PATTERN.test(cleaned)) return 'Enter a valid email address.'
    if (DANGEROUS_PATTERN.test(cleaned)) return 'Invalid characters detected in email.'
    return ''
  }

  if (field === 'message') {
    if (!cleaned) return 'Message is required.'
    if (cleaned.length < 10) return 'Message must be at least 10 characters.'
    if (cleaned.length > 1000) return 'Message cannot exceed 1000 characters.'
    if (DANGEROUS_PATTERN.test(cleaned)) return 'Message contains disallowed content.'
    return ''
  }

  return ''
}

const inputClass = (hasError) =>
  `w-full rounded-lg border bg-white px-3 py-2.5 text-slate-900 focus:outline-none focus:ring-2 dark:bg-slate-800 dark:text-white ${
    hasError
      ? 'border-red-400 focus:ring-red-300 dark:border-red-500'
      : 'border-slate-300 focus:ring-primary/30 dark:border-slate-600'
  }`

const Contact = () => {
  const [form, setForm] = useState(initialForm)
  const [errors, setErrors] = useState(initialErrors)
  const [honeypot, setHoneypot] = useState('')
  const [status, setStatus] = useState({ type: '', text: '' })
  const [sending, setSending] = useState(false)

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }))
    if (status.text) setStatus({ type: '', text: '' })
  }

  const handleBlur = (field) => () => {
    const error = validateField(field, form[field])
    setErrors((prev) => ({ ...prev, [field]: error }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus({ type: '', text: '' })

    if (honeypot) {
      setStatus({ type: 'error', text: 'Unable to send message. Please try again later.' })
      return
    }

    const nextErrors = {
      name: validateField('name', form.name),
      email: validateField('email', form.email),
      message: validateField('message', form.message),
    }

    setErrors(nextErrors)
    if (Object.values(nextErrors).some(Boolean)) return

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY || TEMPLATE_ID === 'your_template_id' || PUBLIC_KEY === 'your_public_key') {
      setStatus({
        type: 'error',
        text: 'Email service is not fully configured. Add your Template ID and Public Key in the .env file.',
      })
      return
    }

    const name = sanitize(form.name)
    const email = sanitize(form.email)
    const message = sanitize(form.message)

    setSending(true)

    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name: name,
          from_email: email,
          user_name: name,
          user_email: email,
          reply_to: email,
          message,
        },
        { publicKey: PUBLIC_KEY }
      )

      setForm(initialForm)
      setStatus({ type: 'success', text: 'Message sent successfully. I will get back to you soon.' })
    } catch {
      setStatus({ type: 'error', text: 'Failed to send message. Please try again or email me directly.' })
    } finally {
      setSending(false)
    }
  }

  return (
    <section id="contact" className="scroll-mt-16 bg-slate-50 py-10 dark:bg-slate-950 md:py-12">
      <div className="mx-auto max-w-6xl px-4">
        <div className="reveal">
          <h2 className="text-center text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
            Let's Build Something Useful
          </h2>
          <div className="mx-auto mt-3 h-1 w-12 rounded bg-primary" />
          <p className="mx-auto mt-4 max-w-2xl text-center text-slate-600 dark:text-slate-400">
            Have an internship, full-time role, freelance task, or collaboration in mind? Send a quick message and I will respond as soon as possible.
          </p>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2 md:gap-8">
          <form className="reveal reveal-delay-1 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800/60 md:p-6" onSubmit={handleSubmit} noValidate>
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                Name
              </label>
              <input
                id="contact-name"
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange('name')}
                onBlur={handleBlur('name')}
                maxLength={50}
                autoComplete="name"
                disabled={sending}
                className={inputClass(errors.name)}
                placeholder="Your name"
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            <div className="mt-3">
              <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                Email
              </label>
              <input
                id="contact-email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange('email')}
                onBlur={handleBlur('email')}
                maxLength={100}
                autoComplete="email"
                disabled={sending}
                className={inputClass(errors.email)}
                placeholder="Your email"
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
            </div>

            <div className="mt-3">
              <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                Message
              </label>
              <textarea
                id="contact-message"
                name="message"
                value={form.message}
                onChange={handleChange('message')}
                onBlur={handleBlur('message')}
                maxLength={1000}
                disabled={sending}
                className={`${inputClass(errors.message)} h-32 resize-none`}
                placeholder="Tell me about the role, project, or opportunity."
              />
              {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
            </div>

            <button
              type="submit"
              disabled={sending}
              className="mt-4 w-full rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? 'Sending...' : 'Send Message'}
            </button>

            {status.text && (
              <p
                className={`text-sm ${
                  status.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-500'
                }`}
              >
                {status.text}
              </p>
            )}
          </form>

          <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800/60 md:p-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Direct contact</p>
              <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Fastest way to reach me
              </h3>
              <div className="mt-5 space-y-3">
                <a
                  href="mailto:omkarkardel175@gmail.com"
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-medium text-slate-800 transition hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-100"
                >
                  Email
                  <span className="text-xs font-normal text-slate-500 dark:text-slate-400">omkarkardel175@gmail.com</span>
                </a>

                <a
                  href="tel:+919309358651"
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-medium text-slate-800 transition hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-100"
                >
                  Phone
                  <span className="text-xs font-normal text-slate-500 dark:text-slate-400">+91 93093 58651</span>
                </a>

                <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-medium text-slate-800 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-100">
                  Location
                  <span className="text-xs font-normal text-slate-500 dark:text-slate-400">Nashik, Maharashtra, India</span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800/60 md:p-6">
              <p className="font-semibold">Availability</p>
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">Open to internships, entry-level roles, and project collaborations.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact
